import React from 'react';

type PandasDataFramePayload = {
  __pandas__: 'dataframe';
  columns?: unknown[];
  index?: unknown[];
  data?: unknown;
};

type PandasSeriesPayload = {
  __pandas__: 'series';
  data?: unknown;
  index?: unknown[];
  name?: string | null;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isPandasDataFrame(value: unknown): value is PandasDataFramePayload {
  return isPlainObject(value) && value.__pandas__ === 'dataframe';
}

function isPandasSeries(value: unknown): value is PandasSeriesPayload {
  return isPlainObject(value) && value.__pandas__ === 'series';
}

function formatRunCell(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function looksLikeRecordRows(value: unknown): value is Record<string, unknown>[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every((row) => isPlainObject(row) && row.__pandas__ == null);
}

function dataframeRows(payload: PandasDataFramePayload): {
  columns: string[];
  index: unknown[] | null;
  rows: Record<string, unknown>[];
} {
  const columns = Array.isArray(payload.columns)
    ? payload.columns.map((c) => String(c))
    : [];
  const data = payload.data;
  let rows: Record<string, unknown>[] = [];
  if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0]) && columns.length) {
    rows = (data as unknown[][]).map((row) => {
      const rec: Record<string, unknown> = {};
      columns.forEach((col, i) => {
        rec[col] = row[i];
      });
      return rec;
    });
  } else if (looksLikeRecordRows(data)) {
    rows = data;
    if (!columns.length) {
      const keys = new Set<string>();
      rows.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
      columns.push(...keys);
    }
  }
  const index = Array.isArray(payload.index) ? payload.index : null;
  return { columns, index, rows };
}

function PandasResultTable({
  columns,
  index,
  rows,
  caption,
}: {
  columns: string[];
  index?: unknown[] | null;
  rows: Record<string, unknown>[];
  caption?: string;
}): React.ReactElement {
  const showIndex = Array.isArray(index) && index.length === rows.length;
  return (
    <div className="sql-result-wrapper pandas-result">
      {caption ? <div className="pandas-result-caption">{caption}</div> : null}
      <table className="sql-result-table">
        <thead>
          <tr>
            {showIndex ? <th className="pandas-index-col" /> : null}
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {showIndex ? (
                <td className="pandas-index-col">{formatRunCell(index[idx])}</td>
              ) : null}
              {columns.map((col) => (
                <td key={col}>{formatRunCell(row[col])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatExampleScalar(value: unknown): string {
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function isTabularRunValue(value: unknown): boolean {
  if (isPandasDataFrame(value) || isPandasSeries(value) || looksLikeRecordRows(value)) {
    return true;
  }
  return (
    isPlainObject(value) &&
    Object.values(value).some(
      (item) => isPandasDataFrame(item) || isPandasSeries(item) || looksLikeRecordRows(item)
    )
  );
}

export function renderPandasRunValue(data: unknown, caption?: string): React.ReactNode {
  if (isPandasDataFrame(data)) {
    const { columns, index, rows } = dataframeRows(data);
    return (
      <PandasResultTable
        columns={columns}
        index={index}
        rows={rows}
        caption={caption ?? 'DataFrame'}
      />
    );
  }
  if (isPandasSeries(data)) {
    const values = Array.isArray(data.data) ? data.data : [];
    const index = Array.isArray(data.index) ? data.index : values.map((_, i) => i);
    const name = data.name ? String(data.name) : '0';
    const rows = values.map((value) => ({ [name]: value }));
    return (
      <PandasResultTable
        columns={[name]}
        index={index}
        rows={rows}
        caption={caption ?? 'Series'}
      />
    );
  }
  if (looksLikeRecordRows(data)) {
    const keys = new Set<string>();
    data.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
    return <PandasResultTable columns={[...keys]} rows={data} caption={caption} />;
  }
  if (isPlainObject(data)) {
    const entries = Object.entries(data);
    if (isTabularRunValue(data)) {
      const preferredKeys = ['index', 'patients', 'cases', 'catalog', 'contacts', 'values'];
      const ordered = [...entries].sort(([keyA, a], [keyB, b]) => {
        const aTable = isPandasDataFrame(a) || isPandasSeries(a) || looksLikeRecordRows(a);
        const bTable = isPandasDataFrame(b) || isPandasSeries(b) || looksLikeRecordRows(b);
        if (aTable !== bTable) return aTable ? 1 : -1;
        const ai = preferredKeys.indexOf(keyA);
        const bi = preferredKeys.indexOf(keyB);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      return (
        <div className="pandas-result-stack">
          {ordered.map(([key, value]) => (
            <div key={key} className="pandas-result-block">
              {isPandasDataFrame(value) || isPandasSeries(value) || looksLikeRecordRows(value) ? (
                renderPandasRunValue(value, key)
              ) : (
                <>
                  <div className="pandas-result-caption">{key}</div>
                  <code className="pandas-result-scalar">{formatExampleScalar(value)}</code>
                </>
              )}
            </div>
          ))}
        </div>
      );
    }
  }
  return <pre className="code-run-output">{JSON.stringify(data)}</pre>;
}
